import { Form, Formik, FormikProps }                         from 'formik';
import React, { FC, useEffect, useMemo, useRef }                        from 'react';
import styled                                                from 'styled-components';
import { Loader }                                            from '../../../../../shared/enhancers/withLoader';
import { FormSpacing, PageHeaderContainer, TextBrand }       from '../../../../../shared/styledComponents';
import { FormFooterActions }                                 from '../../../shared/FormFooterActions';
import { SettingsHeader }                                    from '../../../shared/styles';
import { IFamilyUserPermissions }                            from '../../ManageFamilyAccount/ManageAccountForm/ManageAccountForm';
import { PermissionsFormFields }                             from './PermissionsFormFields';
import { useFetchChatPrivacySettings, useFetchUserPermissions, useUpdateChatPrivacySettings, useUpdateUserPermissions } from './queries';
import { userPermissionsValidationSchema }                   from './validation';
import { useFetchFamilyUser } from '../../../../../shared/queries/family';
import { useFetchSelectedFamily } from '../../../../../shared/components/SelectFamily/queries';
import { LoadingOverlay } from '../../../../../shared/components/LoadingOverlay';
import { useNavigationPrompt } from "../../../../../Layout/hooks/useNavigationPrompt";

interface IMyProfileFormProps {
  memberId?: string;
  familyId?: string;
}

export const SectionLabel = styled(TextBrand)`
  font-size: 18px;
  margin-bottom: 8px !important;
  min-width: 60px;
  line-height: 1.3em !important;
  margin-right: 15px;
  margin-top: 10px;
  font-weight: 500;
  font-family: 'Jost';
`;

export const PermissionsForm: FC<IMyProfileFormProps> = ({
  memberId,
}) => {
  const { data: userPermissions, isFetching } = useFetchUserPermissions(memberId);
  const { mutate: updateUserPermissions, isLoading: updatingUserPermissions } = useUpdateUserPermissions(memberId);
  const { data: family } = useFetchSelectedFamily();
  const { data: user } = useFetchFamilyUser(family?.id);

  const { mutate, isLoading: isLoadingUpdateChatPrivacySettings } = useUpdateChatPrivacySettings();
  const { data: privacySettings, isFetching: isFetchingPrivacySettings } = useFetchChatPrivacySettings(family?.id || '');

  const formikRef = useRef<FormikProps<IFamilyUserPermissions> | null>(null);

  const initialValues = useMemo<IFamilyUserPermissions>(() => ({
    chatAccess         : userPermissions?.chatAccess ?? false,
    viewAboutMe        : userPermissions?.viewAboutMe ?? false,
    editAboutMe        : userPermissions?.editAboutMe ?? false,
    taggedItemsOption  : userPermissions?.taggedItemsOption ?? '',
    geoLocationAccess  : userPermissions?.geoLocationAccess ?? [],
    aboutMeAccess      : userPermissions?.aboutMeAccess ?? [],
    limitingChatsUsers : privacySettings?.data.map((setting: { familyUserId: any; blockedFamilyUserIds: any[] }) => ({
      familyUserId         : setting.familyUserId,
      blockedFamilyUserIds : setting.blockedFamilyUserIds.map((id: any) => ({
        id,
      })),
    })),
  }), [userPermissions, privacySettings]);

  useEffect(() => {
    if (privacySettings && formikRef.current) {
      formikRef.current.resetForm({ values: initialValues });
    }
  }, [privacySettings, initialValues]);

  const submitForm = async (data: IFamilyUserPermissions) => {
    try {
      if (data.limitingChatsUsers) {
        const formattedData = data.limitingChatsUsers.map(
          (user: { familyUserId: string; blockedFamilyUserIds: { id: string }[] }) => ({
            familyUserId         : user.familyUserId,
            blockedFamilyUserIds : user.blockedFamilyUserIds.map(u => u.id),
          })
        );

        await mutate({
          familyId : family?.id || '',
          data     : formattedData,
        });
      }

      const { limitingChatsUsers, ...otherPermissions } = data;

      await updateUserPermissions(otherPermissions, {
        onSuccess: () => {
          formikRef.current?.resetForm({ values: data });
        },
      });
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  const FormContent = (props: FormikProps<IFamilyUserPermissions>) => {
      useNavigationPrompt(
          props.dirty,
          `Are you sure you want to leave this page?\nUnsaved changes in the Customer Info would be lost.`
      );
      return useMemo(() =>
          (<Form onSubmit={props.handleSubmit}>
              <PageHeaderContainer>
                  <SettingsHeader variant="h3">Permissions</SettingsHeader>
              </PageHeaderContainer>

              <FormSpacing>
                  <PermissionsFormFields byInvite accountType={user?.accountType} />
              </FormSpacing>

              <FormFooterActions
                  dirty={props.dirty}
                  isLoading={updatingUserPermissions}
                  formikRef={formikRef}
              />

              <LoadingOverlay loading={isLoadingUpdateChatPrivacySettings} />
          </Form>), [props.dirty, memberId]);
  };

  if (isFetching || isFetchingPrivacySettings) {
    return <Loader minHeight="350px" />;
  }

  return (
    <Formik
      enableReinitialize
      innerRef={formikRef}
      initialValues={initialValues}
      validationSchema={userPermissionsValidationSchema}
      onSubmit={(data) => submitForm(data)}
    >
      {(props) => <FormContent {...props} />}
    </Formik>
  );
};
